import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import SafeImage from '../components/SafeImage';
import useFleetAircraftCount from '../hooks/useFleetAircraftCount';

const partners = [
  ['https://vacchun.hu/', '/images/partners/vacc-hun.png', 'vACCHUN', 'hun'],
  ['https://ezy887.ch/', '/images/partners/simref.png', 'simref', 'simref'],
  ['https://vatsim.net', '/images/partners/vatsim1.png', 'VATSIM', 'vatsim'],
  ['https://inibuilds.com', '/images/partners/inibuilds.png', 'iniBuilds', 'inibuilds'],
  ['https://vacc-slovakia.sk/', '/images/partners/slovakia.png', 'vacc-slovakia', 'slovakia'],
  ['https://xu.ivao.aero/', '/images/partners/ivao-partner-v.png', 'IVAO Partner VA', 'ivao'],
];

function AnimatedCounter({ target, suffix = '' }) {
  const [value, setValue] = useState(0);
  const frameRef = useRef();

  useEffect(() => {
    const duration = 1500;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target]);

  return <>{value.toLocaleString()}{suffix}</>;
}

function MetarTicker() {
  const [reports, setReports] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [status, setStatus] = useState('Loading weather data...');

  useEffect(() => {
    const controller = new AbortController();
    fetch('https://metar.vatsim.net/metar.php?id=LHBP,LROP,EPWA,EGGW,EPGD,LATI', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`METAR request failed: ${response.status}`);
        return response.text();
      })
      .then((data) => {
        const items = data.trim().split(/\r?\n/).filter(Boolean);
        if (!items.length) throw new Error('Empty METAR response');
        setReports(items);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setStatus('METAR Offline');
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!reports.length) return undefined;
    setStatus(reports[0]);
    const interval = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % reports.length);
        setVisible(true);
      }, 500);
    }, 4000);
    return () => window.clearInterval(interval);
  }, [reports]);

  useEffect(() => {
    if (reports.length) setStatus(reports[index]);
  }, [index, reports]);

  return <p id="metar-text" style={{ opacity: visible ? 1 : 0 }}>{status}</p>;
}

export default function HomePage() {
  const fleetCount = useFleetAircraftCount();

  return (
    <>
      <PageTitle>vWZZ - Wizz Air Virtual Airline | Fly The Greenest</PageTitle>

      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-headline">FLY THE <span className="highlight">GREENEST</span></h1>
          <p className="hero-subtext">
            At vWZZ, we bring the thrill of real-world aviation to the virtual skies. Join the most immersive Wizz Air experience on VATSIM and IVAO.
          </p>
          <div className="cta-buttons">
            <a href="#apply" className="btn btn-primary">JOIN THE PINK FLEET</a>
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat-item"><div className="stat-number"><AnimatedCounter target={fleetCount} /></div><div className="stat-label">AIRCRAFT IN FLEET</div></div>
          <div className="stat-item"><div className="stat-number"><AnimatedCounter target={6500} suffix="+" /></div><div className="stat-label">REGISTERED PILOTS</div></div>
          <div className="stat-item"><div className="stat-number">6+</div><div className="stat-label">YEARS OF EXPERIENCE</div></div>
        </div>
        <div className="metar-container"><div className="metar-text-wrapper"><MetarTicker /></div></div>
      </section>

      <section className="features-section">
        <h2 className="section-title">OPERATIONAL EXCELLENCE</h2>
        <div className="features-grid centered-grid">
          <article className="feature-card"><h3 className="feature-title">Real-World FCOM</h3><p className="feature-text">Explore our custom-built FCOM. From detailed SOPs to abnormal procedures, we guide you through every phase of flight with precision.</p></article>
          <article className="feature-card"><h3 className="feature-title">Up-to-date routes</h3><p className="feature-text">Our system continuously pulls real-world data ensuring accurate flight numbers, callsigns, and up-to-date routings.</p></article>
          <article className="feature-card"><h3 className="feature-title">Flight Data Monitoring</h3><p className="feature-text">Our ACARS system analyzes every flight, reviewing fuel compliance, stabilization, and performance to help you fly professionally.</p></article>
        </div>
      </section>

      <section className="etlb-home-note">
        <div className="etlb-home-note-inner">
          <div className="etlb-home-note-copy">
            <h2>vWZZ ELECTRONIC TECHNICAL LOGBOOK</h2>
            <p>
              The vWZZ e-TLB records aircraft technical status by registration
              and maintains continuity of defects, deferred items, servicing
              data and flight records between sectors.
            </p>
            <Link to="/etlb" className="btn btn-secondary">
              VIEW e-TLB OVERVIEW
            </Link>
          </div>
          <Link className="etlb-home-note-visual" to="/etlb" aria-label="View the vWZZ e-TLB overview">
            <img
              src="/images/etlb/preflight-tasks-complete.png"
              alt="Completed vWZZ e-TLB aircraft acceptance task board"
              loading="lazy"
              decoding="async"
            />
          </Link>
        </div>
      </section>

      <section className="etlb-home-note interep-home-note">
        <div className="etlb-home-note-inner">
          <div className="etlb-home-note-copy">
            <h2>vWZZ Interep</h2>
            <p>
              Interep provides structured operational reporting, review
              correspondence and entry assessment within one system.
            </p>
            <Link to="/interep-overview" className="btn btn-secondary">
              View Interep overview
            </Link>
          </div>
          <Link
            className="etlb-home-note-visual"
            to="/interep-overview"
            aria-label="View the vWZZ Interep overview"
          >
            <img
              src="/images/interep/report-forms.png"
              alt="vWZZ Interep operational report form selection"
              loading="lazy"
              decoding="async"
            />
          </Link>
        </div>
      </section>

      <section className="fleet-showcase">
        <div className="showcase-container">
          <div className="showcase-text">
            <h2>OUR <span className="highlight2">Liveries</span></h2>
            <p>Experience the most authentic Wizz Air fleet in flight simulation.<br />Featuring <strong>{fleetCount} real-world airframes</strong> with accurate weathering,<br />custom cabins, up-to-date mismatching panels, and true-to-life<br />aircraft configurations — available for Fenix, FSLabs, and ToLiss.<br />Made by a team of experienced livery painters.</p>
            <div className="cta-buttons showcase-cta"><Link to="/fleet" className="btn btn-primary">ENTER THE HANGAR</Link></div>
          </div>
          <div className="showcase-image"><SafeImage src="/images/livery/LIVERIES.png" alt="Livery preview" fallback="/images/ha-lgq2.png" /></div>
        </div>
      </section>

      <section className="application-section" id="apply">
        <h2 className="section-title">YOUR JOURNEY TO THE LEFT SEAT</h2>
        <div className="steps-container">
          <article className="step-card"><div className="step-number">01</div><h3>Virtual Assessment</h3><p>Start with an assessment covering <strong>Wizz Air history</strong> and basic aviation knowledge.<br /><br /><span className="highlight-text">Requirement: 75% to pass</span></p></article>
          <div className="step-arrow" aria-hidden="true">➜</div>
          <article className="step-card"><div className="step-number">02</div><h3>Study &amp; Explore</h3><p>Unlock access to our operational documents. Use these materials to prepare for an optional <strong>virtual type-rating</strong> exam.</p></article>
          <div className="step-arrow" aria-hidden="true">➜</div>
          <article className="step-card"><div className="step-number">03</div><h3>Virtual Type-Rating</h3><p>A comprehensive exam focused on <strong>procedural details</strong>, aircraft limitations, and SOPs.</p></article>
        </div>
        <div className="apply-cta"><Link to="/apply" className="btn btn-primary">START APPLICATION</Link></div>
      </section>

      <div className="partners-band">
        {partners.map(([href, src, alt, className]) => (
          <a href={href} target="_blank" rel="noopener noreferrer" key={href}><img src={src} className={`logo ${className}`} alt={alt} /></a>
        ))}
      </div>
    </>
  );
}
